/**
 * RiskApplicationPage
 * Main page for managing risk applications across organizational levels
 *
 * @version 2025.1.0
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
  Badge,
  Chip,
  Stack,
  ListItemText,
} from '@mui/material';
import {
  Business as CompanyIcon,
  Domain as DepartmentIcon,
  Work as OccupationIcon,
  Person as EmployeeIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { PageShell } from '../../components';
import { DualTransferList, RiskApplicationModal, ExposureLevelBadge } from '../../components/risk-applications';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { occupationService } from '../../services/occupationService';
import { employeeService } from '../../services/employeeService';
import {
  ApplicationLevel,
  ApplicationLevelLabels,
  ExposureLevel,
} from '../../types/riskApplication';
import type { RiskApplicationScope } from '../../types/riskApplication';
import { empresaDaBusca } from './empresaDaBusca';
import { useRiskApplications } from './hooks/useRiskApplications';
import type {
  RiskWithApplication,
  AvailableRisk,
} from './hooks/useRiskApplications';

function isRiskWithApplication(
  item: RiskWithApplication | AvailableRisk
): item is RiskWithApplication {
  return 'applicationLevel' in item && 'exposureLevel' in item;
}

// ============================================================================
// TYPES
// ============================================================================

interface SelectOption {
  id: number;
  name: string;
  parentId?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function TabPanel({ children, value, index }: TabPanelProps): React.ReactElement | null {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

const levelIcons: Record<ApplicationLevel, React.ReactElement> = {
  [ApplicationLevel.COMPANY]: <CompanyIcon />,
  [ApplicationLevel.DEPARTMENT]: <DepartmentIcon />,
  [ApplicationLevel.OCCUPATION]: <OccupationIcon />,
  [ApplicationLevel.EMPLOYEE]: <EmployeeIcon />,
};

// ============================================================================
// COMPONENT
// ============================================================================

export const RiskApplicationPage: React.FC = () => {
  // Risk application state from hook
  const {
    applied,
    available,
    alerts,
    loading,
    loadingAlerts,
    error,
    alertsError,
    loadRisks,
    loadAlerts,
    applyMultipleRisks,
    removeMultipleRisks,
    clearError,
    clearAlertsError,
  } = useRiskApplications();

  const { search } = useLocation();

  // Local UI state
  const [activeTab, setActiveTab] = useState(0);
  const [selectedLevel, setSelectedLevel] = useState<ApplicationLevel>(
    ApplicationLevel.COMPANY
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(() =>
    empresaDaBusca(search)
  );
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [selectedOccupationId, setSelectedOccupationId] = useState<number | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [highlightField, setHighlightField] = useState<string | null>(null);

  // Options for selectors
  const [companies, setCompanies] = useState<SelectOption[]>([]);
  const [departments, setDepartments] = useState<SelectOption[]>([]);
  const [occupations, setOccupations] = useState<SelectOption[]>([]);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'apply' | 'edit'>('apply');
  const [selectedRisk, setSelectedRisk] = useState<
    AvailableRisk | RiskWithApplication | null
  >(null);
  const [pendingApplyRisks, setPendingApplyRisks] = useState<AvailableRisk[]>([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================================================
  // LOAD OPTIONS
  // ============================================================================

  // Load companies
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoadingOptions(true);
        const response = await companyService.getCompanies({ limit: 1000 });
        setCompanies(
          response.data.map((c) => ({
            id: Number(c.id),
            name: c.tradeName || c.legalName,
          }))
        );
      } catch (err) {
        console.error('Error loading companies:', err);
      } finally {
        setLoadingOptions(false);
      }
    };
    loadCompanies();
  }, []);

  // Load departments when company changes
  useEffect(() => {
    if (!selectedCompanyId) {
      setDepartments([]);
      return;
    }

    const loadDepartments = async () => {
      try {
        const response = await departmentService.getDepartments({
          companyId: String(selectedCompanyId),
          limit: 1000,
        });
        setDepartments(
          response.data.map((d) => ({
            id: Number(d.id),
            name: d.name,
            parentId: Number(d.companyId),
          }))
        );
      } catch (err) {
        console.error('Error loading departments:', err);
      }
    };
    loadDepartments();
  }, [selectedCompanyId]);

  // Load occupations when department changes
  useEffect(() => {
    if (!selectedDepartmentId) {
      setOccupations([]);
      return;
    }

    const loadOccupations = async () => {
      try {
        const response = await occupationService.getOccupations({
          limit: 1000,
          filters: { departmentId: selectedDepartmentId },
        });
        setOccupations(
          response.data.map((o) => ({
            id: o.id,
            name: o.name,
            parentId: o.departmentId,
          }))
        );
      } catch (err) {
        console.error('Error loading occupations:', err);
      }
    };
    loadOccupations();
  }, [selectedDepartmentId]);

  // Load employees when occupation changes
  useEffect(() => {
    if (!selectedOccupationId) {
      setEmployees([]);
      return;
    }

    const loadEmployees = async () => {
      try {
        const response = await employeeService.getEmployees({
          limit: 1000,
          filters: { occupationId: selectedOccupationId },
        });
        setEmployees(
          response.data.map((e) => ({
            id: e.id,
            name: e.name,
            parentId: e.occupationId,
          }))
        );
      } catch (err) {
        console.error('Error loading employees:', err);
      }
    };
    loadEmployees();
  }, [selectedOccupationId]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const currentTargetId = useMemo((): number | null => {
    switch (selectedLevel) {
      case ApplicationLevel.COMPANY:
        return selectedCompanyId;
      case ApplicationLevel.DEPARTMENT:
        return selectedDepartmentId;
      case ApplicationLevel.OCCUPATION:
        return selectedOccupationId;
      case ApplicationLevel.EMPLOYEE:
        return selectedEmployeeId;
    }
  }, [
    selectedLevel,
    selectedCompanyId,
    selectedDepartmentId,
    selectedOccupationId,
    selectedEmployeeId,
  ]);

  const currentTargetName = useMemo((): string => {
    switch (selectedLevel) {
      case ApplicationLevel.COMPANY:
        return companies.find((c) => c.id === selectedCompanyId)?.name || '';
      case ApplicationLevel.DEPARTMENT:
        return departments.find((d) => d.id === selectedDepartmentId)?.name || '';
      case ApplicationLevel.OCCUPATION:
        return occupations.find((o) => o.id === selectedOccupationId)?.name || '';
      case ApplicationLevel.EMPLOYEE:
        return employees.find((e) => e.id === selectedEmployeeId)?.name || '';
    }
  }, [
    selectedLevel,
    selectedCompanyId,
    selectedDepartmentId,
    selectedOccupationId,
    selectedEmployeeId,
    companies,
    departments,
    occupations,
    employees,
  ]);

  const getEmptyStateMessage = (): string => {
    if (!selectedCompanyId) return 'Selecione uma empresa para gerenciar os riscos.';
    const missing: string[] = [];
    if (selectedLevel !== ApplicationLevel.COMPANY && !selectedDepartmentId) missing.push('um setor');
    if ((selectedLevel === ApplicationLevel.OCCUPATION || selectedLevel === ApplicationLevel.EMPLOYEE) && !selectedOccupationId) missing.push('um cargo');
    if (selectedLevel === ApplicationLevel.EMPLOYEE && !selectedEmployeeId) missing.push('um funcionário');
    if (missing.length === 0) return 'Selecione um destino para gerenciar os riscos.';
    return `Selecione ${missing.join(', ')} para gerenciar os riscos.`;
  };

  const alertsCounts = useMemo(() => {
    if (!alerts) return { critical: 0, veryHigh: 0, high: 0 };
    return {
      critical: alerts.summary?.totalCritical || 0,
      veryHigh: alerts.summary?.veryHighCount || 0,
      high: alerts.summary?.highCount || 0,
    };
  }, [alerts]);

  // ============================================================================
  // LOAD RISKS WHEN TARGET CHANGES
  // ============================================================================

  useEffect(() => {
    if (currentTargetId) {
      loadRisks(selectedLevel, currentTargetId);
    }
  }, [selectedLevel, currentTargetId, loadRisks]);

  // Load alerts when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      loadAlerts(selectedCompanyId);
    }
  }, [selectedCompanyId, loadAlerts]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const pulseAnimation = {
    '@keyframes borderPulse': {
      '0%': { borderColor: 'error.main', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
      '50%': { borderColor: 'error.main', boxShadow: '0 0 0 4px rgba(211, 47, 47, 0.1)' },
      '100%': { borderColor: 'divider', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      animation: 'borderPulse 1.5s ease-in-out infinite',
    },
  };

  const triggerHighlight = (field: string) => {
    setHighlightField(field);
  };

  const handleLevelChange = (level: ApplicationLevel) => {
    setSelectedLevel(level);
    // Reset dependent selections and highlight the next field to fill
    if (level === ApplicationLevel.COMPANY) {
      setSelectedDepartmentId(null);
      setSelectedOccupationId(null);
      setSelectedEmployeeId(null);
    } else if (level === ApplicationLevel.DEPARTMENT) {
      setSelectedOccupationId(null);
      setSelectedEmployeeId(null);
      triggerHighlight('department');
    } else if (level === ApplicationLevel.OCCUPATION) {
      setSelectedEmployeeId(null);
      if (!selectedDepartmentId) {
        triggerHighlight('department');
      } else {
        triggerHighlight('occupation');
      }
    } else if (level === ApplicationLevel.EMPLOYEE) {
      if (!selectedDepartmentId) {
        triggerHighlight('department');
      } else if (!selectedOccupationId) {
        triggerHighlight('occupation');
      } else {
        triggerHighlight('employee');
      }
    }
  };

  const handleApply = useCallback(
    async (items: (AvailableRisk | RiskWithApplication)[]) => {
      const availableItems = items.filter(
        (item): item is AvailableRisk => !isRiskWithApplication(item)
      );

      if (availableItems.length === 0) return;

      if (availableItems.length === 1) {
        // Single risk: open modal for detailed configuration
        setSelectedRisk(availableItems[0]);
        setPendingApplyRisks(availableItems);
        setModalMode('apply');
        setModalOpen(true);
        return;
      }

      // Multiple risks: bulk apply with defaults — no modal
      if (!currentTargetId) return;

      const riskIds = availableItems.map((item) => item.id);
      const startDate = new Date().toISOString().split('T')[0];
      const result = await applyMultipleRisks(selectedLevel, currentTargetId, riskIds, {
        startDate,
      });

      if (result !== false) {
        const { created, duplicates } = result;
        const message =
          duplicates > 0
            ? `${created} aplicado(s), ${duplicates} já existiam`
            : `${created} risco(s) aplicado(s) com sucesso`;
        setSnackbar({ open: true, message, severity: 'success' });
      }
    },
    [currentTargetId, selectedLevel, applyMultipleRisks]
  );

  const handleRemove = useCallback(
    async (items: (AvailableRisk | RiskWithApplication)[]) => {
      if (!currentTargetId) return;

      const appliedItems = items.filter(
        (item): item is RiskWithApplication => isRiskWithApplication(item)
      );

      if (appliedItems.length === 0) return;

      const riskIds = appliedItems.map((item) => item.riskId);
      const success = await removeMultipleRisks(selectedLevel, currentTargetId, riskIds);

      if (success) {
        setSnackbar({
          open: true,
          message: `${riskIds.length} risco(s) removido(s) com sucesso`,
          severity: 'success',
        });
      }
    },
    [currentTargetId, selectedLevel, removeMultipleRisks]
  );

  const levelToScope: Record<ApplicationLevel, RiskApplicationScope> = useMemo(() => ({
    [ApplicationLevel.COMPANY]: 'company',
    [ApplicationLevel.DEPARTMENT]: 'department',
    [ApplicationLevel.OCCUPATION]: 'occupation',
    [ApplicationLevel.EMPLOYEE]: 'employee',
  }), []);

  const handleSaved = useCallback(() => {
    if (!currentTargetId) return;
    loadRisks(selectedLevel, currentTargetId);
    setSnackbar({
      open: true,
      message: modalMode === 'apply' ? 'Risco aplicado com sucesso' : 'Aplicação atualizada com sucesso',
      severity: 'success',
    });
    setModalOpen(false);
    setPendingApplyRisks([]);
    setSelectedRisk(null);
  }, [currentTargetId, selectedLevel, loadRisks, modalMode]);

  const handleItemEdit = useCallback(
    (item: AvailableRisk | RiskWithApplication) => {
      if (!isRiskWithApplication(item)) return;

      setSelectedRisk(item);
      setModalMode('edit');
      setModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setPendingApplyRisks([]);
    setSelectedRisk(null);
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================

  const renderLevelSelector = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Selecionar Nível e Destino" />
      <CardContent>
        <Grid container spacing={3}>
          {/* Level Selector */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth>
              <InputLabel>Nível</InputLabel>
              <Select
                value={selectedLevel}
                label="Nível"
                onChange={(e) => handleLevelChange(e.target.value as ApplicationLevel)}
              >
                {Object.values(ApplicationLevel).map((level) => (
                  <MenuItem key={level} value={level}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {levelIcons[level]}
                      <span>{ApplicationLevelLabels[level]}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Company Selector (always shown) */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={selectedCompanyId || ''}
                label="Empresa"
                onChange={(e) => {
                  setSelectedCompanyId(Number(e.target.value));
                  setSelectedDepartmentId(null);
                  setSelectedOccupationId(null);
                  setSelectedEmployeeId(null);
                }}
                disabled={loadingOptions}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Department Selector (shown for department, occupation, employee) */}
          {selectedLevel !== ApplicationLevel.COMPANY && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth sx={highlightField === 'department' ? pulseAnimation : undefined}>
                <InputLabel>Setor</InputLabel>
                <Select
                  value={selectedDepartmentId || ''}
                  label="Setor"
                  onChange={(e) => {
                    setSelectedDepartmentId(Number(e.target.value));
                    setSelectedOccupationId(null);
                    setSelectedEmployeeId(null);
                    if (selectedLevel === ApplicationLevel.OCCUPATION || selectedLevel === ApplicationLevel.EMPLOYEE) {
                      triggerHighlight('occupation');
                    } else {
                      setHighlightField(null);
                    }
                  }}
                  disabled={!selectedCompanyId || departments.length === 0}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Occupation Selector (shown for occupation, employee) */}
          {(selectedLevel === ApplicationLevel.OCCUPATION ||
            selectedLevel === ApplicationLevel.EMPLOYEE) && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth sx={highlightField === 'occupation' ? pulseAnimation : undefined}>
                <InputLabel>Cargo</InputLabel>
                <Select
                  value={selectedOccupationId || ''}
                  label="Cargo"
                  onChange={(e) => {
                    setSelectedOccupationId(Number(e.target.value));
                    setSelectedEmployeeId(null);
                    if (selectedLevel === ApplicationLevel.EMPLOYEE) {
                      triggerHighlight('employee');
                    } else {
                      setHighlightField(null);
                    }
                  }}
                  disabled={!selectedDepartmentId || occupations.length === 0}
                >
                  {occupations.map((occ) => (
                    <MenuItem key={occ.id} value={occ.id}>
                      {occ.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Employee Selector (shown for employee only) */}
          {selectedLevel === ApplicationLevel.EMPLOYEE && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth sx={highlightField === 'employee' ? pulseAnimation : undefined}>
                <InputLabel>Funcionário</InputLabel>
                <Select
                  value={selectedEmployeeId || ''}
                  label="Funcionário"
                  onChange={(e) => {
                    setSelectedEmployeeId(Number(e.target.value));
                    setHighlightField(null);
                  }}
                  disabled={!selectedOccupationId || employees.length === 0}
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        {/* Current Target Info */}
        {currentTargetId && currentTargetName && (
          <Box sx={{ mt: 2 }}>
            <Chip
              icon={levelIcons[selectedLevel]}
              label={`${ApplicationLevelLabels[selectedLevel]}: ${currentTargetName}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderRiskTransferList = () => (
    <Card>
      <CardHeader
        title="Gerenciar Riscos"
        subheader={
          currentTargetId
            ? `Aplicar ou remover riscos de ${ApplicationLevelLabels[selectedLevel].toLowerCase()}`
            : 'Selecione um destino para gerenciar riscos'
        }
      />
      <CardContent>
        {!currentTargetId ? (
          <Alert severity="info">{getEmptyStateMessage()}</Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DualTransferList
            available={available}
            applied={applied}
            onApply={handleApply}
            onRemove={handleRemove}
            getItemKey={(item) => (isRiskWithApplication(item) ? item.riskId : item.id)}
            getItemLabel={(item) => (isRiskWithApplication(item) ? item.riskName : item.risk)}
            getItemSecondary={(item) => item.riskFactorName}
            searchFields={['riskFactorName', 'hazard']}
            availableTitle="Riscos Disponíveis"
            appliedTitle="Riscos Aplicados"
            loading={loading}
            renderItem={(item, isApplied) => (
              <ListItemText
                primary={isRiskWithApplication(item) ? item.riskName : item.risk}
                secondary={
                  isApplied && isRiskWithApplication(item) ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <ExposureLevelBadge
                        level={item.exposureLevel}
                        size="small"
                        exposureDays={item.exposureDays}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {item.riskFactorName}
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={item.riskFactorName} size="small" variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        {item.hazard}
                      </Typography>
                    </Stack>
                  )
                }
              />
            )}
            onItemEdit={handleItemEdit}
          />
        )}
      </CardContent>
    </Card>
  );

  const renderAlertsTab = () => (
    <Card>
      <CardHeader
        title="Alertas de Exposição Crítica"
        subheader="Riscos com nível de exposição alto ou muito alto"
      />
      <CardContent>
        {!selectedCompanyId ? (
          <Alert severity="info">
            Selecione uma empresa para visualizar os alertas de exposição crítica.
          </Alert>
        ) : loadingAlerts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : alertsError ? (
          <Alert severity="error" onClose={clearAlertsError}>
            {alertsError}
          </Alert>
        ) : !alerts || alerts.critical.length === 0 ? (
          <Alert severity="success">
            Nenhum risco com nível de exposição crítico encontrado.
          </Alert>
        ) : (
          <Box>
            {/* Summary */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <Chip
                label={`Total: ${alertsCounts.critical}`}
                color="default"
              />
              <Chip
                label={`Muito Alto: ${alertsCounts.veryHigh}`}
                color="error"
              />
              <Chip
                label={`Alto: ${alertsCounts.high}`}
                color="warning"
              />
            </Box>

            {/* Critical Alerts List */}
            {alerts.critical.map((alert) => (
              <Card
                key={`critical-${alert.applicationId}`}
                variant="outlined"
                sx={{
                  mb: 1,
                  borderColor:
                    alert.exposureLevel === ExposureLevel.VERY_HIGH
                      ? 'error.main'
                      : 'warning.main',
                }}
              >
                <CardContent sx={{ py: 1.5 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="subtitle2">{alert.riskName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ApplicationLevelLabels[alert.targetType]}: {alert.targetName}
                      </Typography>
                    </Box>
                    <ExposureLevelBadge
                      level={alert.exposureLevel}
                      exposureDays={alert.exposureDays}
                    />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Medicina', path: '/medicina' },
        { label: 'Aplicação de Riscos' },
      ]}
      title="Aplicação de Riscos Ocupacionais"
      subtitle="Gerencie a aplicação de riscos em empresas, setores, cargos e funcionários"
    >
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Level Selector */}
      {renderLevelSelector()}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Gerenciar Riscos" />
          <Tab
            label={
              <Badge
                badgeContent={alertsCounts.critical}
                color="error"
                max={99}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon fontSize="small" />
                  Alertas Críticos
                </Box>
              </Badge>
            }
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {renderRiskTransferList()}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {renderAlertsTab()}
      </TabPanel>

      {/* Modal */}
      {modalOpen && currentTargetId != null && (
        modalMode === 'apply' && pendingApplyRisks.length > 0 ? (
          <RiskApplicationModal
            open={modalOpen}
            onClose={handleCloseModal}
            onSaved={handleSaved}
            scope={levelToScope[selectedLevel]}
            scopeId={currentTargetId}
            mode="apply"
            riskId={pendingApplyRisks[0].id}
            riskName={pendingApplyRisks[0].risk}
            riskFactorName={pendingApplyRisks[0].riskFactorName}
          />
        ) : modalMode === 'edit' && selectedRisk != null && isRiskWithApplication(selectedRisk) ? (
          <RiskApplicationModal
            open={modalOpen}
            onClose={handleCloseModal}
            onSaved={handleSaved}
            scope={levelToScope[selectedLevel]}
            scopeId={currentTargetId}
            mode="edit"
            applicationId={selectedRisk.id}
            riskName={selectedRisk.riskName}
            riskFactorName={selectedRisk.riskFactorName}
          />
        ) : null
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default RiskApplicationPage;
